import cx from "classnames"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { usePageAssets } from "hooks/usePageAssets"
import ContactSalesModal from "components/ContactSalesModal"
import { openModal } from "utils/openModal"
import MarkdownContent from "components/MarkdownContent"
import ImageCarousel from "components/ui/ImageCarousel"
import Button from "components/ui/Button"
import Container from "components/ui/Container"
import Heading from "components/ui/Heading"
import Image from "components/ui/Image"
import * as css from "./TwoSideImageAndText.module.css"

function TwoSideImageAndText({
  heading,
  subheading,
  subtitle: subHeading,
  ctaButtons,
  image,
  imageLink,
  imageSize,
  imageShadow,
  carousel,
  flipped,
  inverted,
  heroSection,
  decorated,
}) {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const contactSourcePage =
    ctaButtons?.find((b) => b.contactModal)?.contactModalSource || ""
  const { getAsset } = usePageAssets()
  const wavesDecoration = decorated ? getAsset("abstract-waves-svg") : null
  return (
    <div
      className={cx({
        "my-20 bg-big-stone py-16 lg:py-28": inverted && !heroSection,
        "mb-20 bg-big-stone pb-16 pt-32 lg:pb-20 lg:pt-56":
          inverted && heroSection,
        "mb-20 mt-32 lg:mt-48": !inverted && heroSection,
        "my-20": !inverted && !heroSection,
      })}
    >
      <Container>
        <div
          className={cx(
            "flex flex-col items-center gap-x-20 gap-y-10",
            flipped ? "lg:flex-row-reverse" : "lg:flex-row"
          )}
        >
          <div
            className={cx("w-full", {
              "lg:w-1/2": imageSize !== "Small",
              "lg:w-2/3": imageSize === "Small",
            })}
          >
            <Heading
              title={heading}
              subtitle={
                subheading ? null : (
                  <MarkdownContent
                    className="space-y-3"
                    rawMarkdownBody={
                      subHeading?.childMarkdownRemark?.rawMarkdownBody
                    }
                  />
                )
              }
              inverted={inverted}
            />
            {subheading && (
              <>
                <p
                  className={cx(
                    "mt-3 text-2xl font-semibold md:text-3xl",
                    inverted ? "text-white" : "text-anchor"
                  )}
                >
                  {subheading}
                </p>
                <MarkdownContent
                  className="mt-4 space-y-3 text-xl text-dark-grey md:text-2xl"
                  rawMarkdownBody={
                    subHeading?.childMarkdownRemark?.rawMarkdownBody
                  }
                />
              </>
            )}
            {ctaButtons && (
              <div className="mt-6 flex gap-4">
                {ctaButtons.map(
                  (
                    { buttonLabel, color, url, trackingName, contactModal },
                    index
                  ) => (
                    <Button
                      key={index}
                      href={url}
                      color={color}
                      trackClick={trackingName}
                      onClick={
                        contactModal
                          ? openModal(setIsContactModalOpen)
                          : undefined
                      }
                      size="medium"
                      nowrap
                    >
                      {buttonLabel}
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
          {carousel?.length > 0 ? (
            <div className="relative w-full lg:w-1/2">
              <ImageCarousel images={carousel} />
            </div>
          ) : (
            image && (
              <div
                className={cx("relative w-full", {
                  "lg:w-1/2": imageSize !== "Small",
                  "lg:w-1/3": imageSize === "Small",
                  [css.imageHuge]: imageSize === "Huge",
                })}
              >
                {wavesDecoration && (
                  <div className="absolute top-1/2 -right-10 -z-10 hidden w-[200%] -translate-y-1/4 lg:block">
                    <Image src={wavesDecoration} />
                  </div>
                )}
                {imageLink ? (
                  <a href={imageLink}>
                    <Image
                      src={image}
                      className={cx("w-full", {
                        [css.imageShadow]: imageShadow,
                      })}
                    />
                  </a>
                ) : (
                  <Image
                    src={image}
                    className={cx("w-full", {
                      [css.imageShadow]: imageShadow,
                    })}
                  />
                )}
              </div>
            )
          )}
        </div>
      </Container>
      <ContactSalesModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        sourcePage={contactSourcePage}
      />
    </div>
  )
}

TwoSideImageAndText.propTypes = {
  heading: PropTypes.string,
  subheading: PropTypes.string,
  subtitle: PropTypes.shape({}),
  image: PropTypes.shape({}),
  imageLink: PropTypes.string,
  imageSize: PropTypes.oneOf(["Small", "Regular", "Huge"]),
  /** If true, apply a soft drop shadow to the image */
  imageShadow: PropTypes.bool,
  carousel: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      caption: PropTypes.string,
    })
  ),
  ctaButtons: PropTypes.arrayOf(
    PropTypes.shape({
      buttonLabel: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      trackingName: PropTypes.string,
    })
  ),
  flipped: PropTypes.bool,
  inverted: PropTypes.bool,
  heroSection: PropTypes.bool,
  /** If true, show decorative waves behind the image */
  decorated: PropTypes.bool,
}

export default TwoSideImageAndText

export const query = graphql`
  fragment TwoSideImageAndText on ContentfulTwoSideImageAndText {
    id
    __typename
    heading
    subtitle {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
    ctaButtons {
      buttonLabel
      color
      url
      trackingName
    }
    image {
      gatsbyImageData(quality: 100, placeholder: BLURRED)
      description
    }
    flipped
    inverted
    heroSection
  }
`
